/**
 * ProjectMembersTab — the manager's status board.
 *
 * The question this answers is not "who is in the project" but "where does each
 * invitation stand, and is anyone actually turning up". So every row carries
 * three independent facts, and they are shown together rather than being
 * conflated into one status:
 *
 *   • the invitation state — accepted / pending / declined, or "via
 *     organization" for a whole-tenant grant, which has no accept step;
 *   • whether that person has actually written anything here;
 *   • which organization they belong to, because members span tenants.
 *
 * Resend is offered per person AND in bulk, since chasing eight pending
 * invitations one click at a time is the thing a manager will not do.
 */

import { useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import projectService from '../services/projectService';
import type { ProjectMemberRow, ProjectSummary } from '../types';
import { PersonAvatar } from './PersonAvatar';
import { EmptyBlock } from './StateBlocks';
import './ProjectTabs.css';

interface ProjectMembersTabProps {
  project: ProjectSummary;
  members: ProjectMemberRow[];
  onMembersChanged: (members: ProjectMemberRow[]) => void;
  onInvite: () => void;
}

export function ProjectMembersTab({
  project,
  members,
  onMembersChanged,
  onInvite,
}: ProjectMembersTabProps) {
  const { t } = useLanguage();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<ProjectMemberRow | null>(null);
  const [removing, setRemoving] = useState(false);

  const canManage = project.can_manage && !project.is_archived;

  const chaseable = useMemo(
    () => members.filter(m => !m.is_tenant_grant
      && (m.status === 'pending' || m.status === 'declined' || m.status === 'left')),
    [members],
  );

  const run = async (id: string, action: () => Promise<ProjectMemberRow[]>) => {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      onMembersChanged(await action());
    } catch (err: any) {
      setError(err?.response?.data?.error
        || (t('projects.members.actionFailed') || 'That did not work. Please try again.'));
    } finally {
      setBusyId(null);
    }
  };

  const resendAll = async () => {
    setBulkBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { resentCount, members: next } = await projectService.resendAllInvitations(
        project.project_id,
      );
      onMembersChanged(next);
      setNotice((t('projects.members.resentCount') || 'Re-sent {{n}} invitations.')
        .replace('{{n}}', String(resentCount)));
    } catch (err: any) {
      setError(err?.response?.data?.error
        || (t('projects.members.actionFailed') || 'That did not work. Please try again.'));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    setError(null);
    try {
      onMembersChanged(await projectService.removeMember(
        project.project_id, pendingRemove.member_id,
      ));
      setPendingRemove(null);
    } catch (err: any) {
      setError(err?.response?.data?.error
        || (t('projects.members.actionFailed') || 'That did not work. Please try again.'));
    } finally {
      setRemoving(false);
    }
  };

  const statusPill = (member: ProjectMemberRow) => {
    if (member.is_tenant_grant) {
      return (
        <span className="pj-pill pj-pill-accent">
          {t('projects.members.viaOrganization') || 'Whole organization'}
        </span>
      );
    }
    if (member.status === 'accepted') {
      return (
        <span className="pj-pill pj-pill-accepted">
          {t('projects.members.accepted') || 'Accepted'}
        </span>
      );
    }
    if (member.status === 'declined') {
      return (
        <span className="pj-pill pj-pill-declined">
          {t('projects.members.declined') || 'Declined'}
        </span>
      );
    }
    if (member.status === 'left') {
      // Distinct from "declined" and from a revoked grant: they were in, and
      // they chose to go. A manager can ask them back.
      return (
        <span className="pj-pill pj-pill-neutral">
          {t('projects.members.left') || 'Left the project'}
        </span>
      );
    }
    return (
      <span className="pj-pill pj-pill-pending">
        {t('projects.members.pending') || 'Pending'}
      </span>
    );
  };

  return (
    <div className="pj-tab members-tab">
      <div className="members-toolbar">
        <div className="members-summary">
          <span>
            {(t('projects.members.summary') || '{{n}} with access')
              .replace('{{n}}', String(members.filter(m => m.status === 'accepted').length + 1))}
          </span>
          {chaseable.length > 0 && (
            <span className="members-summary-pending">
              {(t('projects.members.awaiting') || '{{n}} awaiting an answer')
                .replace('{{n}}', String(chaseable.length))}
            </span>
          )}
        </div>
        {canManage && (
          <div className="members-toolbar-actions">
            {chaseable.length > 0 && (
              <button
                type="button"
                className="pj-btn pj-btn-secondary pj-btn-sm"
                onClick={resendAll}
                disabled={bulkBusy}
              >
                {bulkBusy
                  ? (t('common.saving') || 'Saving…')
                  : (t('projects.members.resendAll') || 'Re-send to everyone waiting')}
              </button>
            )}
            {/* Suppressed while the empty state is showing: that panel carries
                the same action as its primary call, and two identical buttons
                a few hundred pixels apart reads as an oversight. */}
            {members.length > 0 && (
              <button type="button" className="pj-btn pj-btn-primary pj-btn-sm" onClick={onInvite}>
                {t('projects.members.invite') || 'Invite people'}
              </button>
            )}
          </div>
        )}
      </div>

      {error && <div className="pj-form-error">{error}</div>}
      {notice && <div className="members-notice" role="status">{notice}</div>}

      {/* The creator is not a project_members row — they are the owner. Showing
          them first makes the board a complete answer to "who is here". */}
      <ul className="members-list">
        <li className="members-row">
          <PersonAvatar
            seed={project.created_by_user_id}
            name={project.created_by_name}
            email={project.created_by_email}
            tenantName={project.owner_tenant_name}
            size={34}
          />
          <span className="members-row-text">
            <span className="members-row-name" dir="auto">
              {project.created_by_name || project.created_by_email}
            </span>
            <span className="members-row-sub" dir="auto">
              {project.owner_tenant_name}
            </span>
          </span>
          <span className="pj-pill pj-pill-accent">{t('projects.members.owner') || 'Owner'}</span>
        </li>

        {members.map(member => (
          <li key={member.member_id} className="members-row">
            {member.is_tenant_grant ? (
              <span className="members-row-org" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 21h18M5 21V7l7-4 7 4v14M10 21v-5h4v5" />
                </svg>
              </span>
            ) : (
              <PersonAvatar
                seed={member.member_user_id}
                name={member.member_name}
                email={member.member_email}
                tenantName={member.member_tenant_name}
                size={34}
                contributed={member.has_contributed === true}
              />
            )}

            <span className="members-row-text">
              <span className="members-row-name">
                {member.member_name || member.member_email}
                {member.member_role === 'manager' && (
                  <span className="pj-pill pj-pill-neutral members-role-pill">
                    {t('projects.members.manager') || 'Manager'}
                  </span>
                )}
              </span>
              <span dir="auto" className="members-row-sub">
                {/* A tenant grant's name IS the organization, so repeating the
                    tenant label under it just says "Team Alpha / Team Alpha".
                    Say what the grant actually means instead. */}
                {member.is_tenant_grant
                  ? (t('projects.members.everyoneIn') || 'Everyone in this organization')
                  : member.member_tenant_name}
                {!member.is_tenant_grant && (
                  <>
                    {' · '}
                    {member.has_contributed
                      ? (t('projects.members.hasContributed') || 'Has contributed')
                      : (t('projects.members.noContribution') || 'Has not written yet')}
                  </>
                )}
                {member.reminded_at && member.status === 'pending' && (
                  <> · {t('projects.members.reminded') || 'reminder sent'}</>
                )}
              </span>
            </span>

            {statusPill(member)}

            {canManage && (
              <span className="members-row-actions">
                {!member.is_tenant_grant
                  && (member.status === 'pending' || member.status === 'declined'
                      || member.status === 'left') && (
                  <button
                    type="button"
                    className="pj-btn pj-btn-ghost pj-btn-sm"
                    onClick={() => run(member.member_id,
                      () => projectService.resendInvitation(project.project_id, member.member_id))}
                    disabled={busyId === member.member_id}
                  >
                    {t('projects.members.resend') || 'Re-send'}
                  </button>
                )}

                {!member.is_tenant_grant && member.status === 'accepted' && (
                  <button
                    type="button"
                    className="pj-btn pj-btn-ghost pj-btn-sm"
                    onClick={() => run(member.member_id,
                      () => projectService.setMemberRole(
                        project.project_id,
                        member.member_id,
                        member.member_role === 'manager' ? 'contributor' : 'manager',
                      ))}
                    disabled={busyId === member.member_id}
                  >
                    {member.member_role === 'manager'
                      ? (t('projects.members.demote') || 'Remove manager')
                      : (t('projects.members.promote') || 'Make manager')}
                  </button>
                )}

                <button
                  type="button"
                  className="pj-icon-btn"
                  onClick={() => setPendingRemove(member)}
                  disabled={busyId === member.member_id}
                  aria-label={t('projects.members.remove') || 'Revoke access'}
                  title={t('projects.members.remove') || 'Revoke access'}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>

      {members.length === 0 && (
        <EmptyBlock
          icon={(
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          )}
          title={t('projects.members.emptyTitle') || 'Just you so far'}
          body={canManage
            ? (t('projects.members.emptyBodyManager')
              || 'Invite the people working on this — from your organization, or from one beneath it.')
            : (t('projects.members.emptyBodyMember')
              || 'Nobody else has been invited to this project yet.')}
          action={canManage ? (
            <button type="button" className="pj-btn pj-btn-primary" onClick={onInvite}>
              {t('projects.members.invite') || 'Invite people'}
            </button>
          ) : undefined}
        />
      )}

      <ConfirmModal
        isOpen={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={handleRemove}
        title={t('projects.members.removeTitle') || 'Revoke access?'}
        message={(t('projects.members.removeBody')
          || '“{{name}}” will lose access to this project immediately. Anything they already contributed stays.')
          .replace('{{name}}', pendingRemove?.member_name || pendingRemove?.member_email || '')}
        confirmText={t('projects.members.remove') || 'Revoke access'}
        cancelText={t('common.cancel') || 'Cancel'}
        variant="danger"
        loading={removing}
      />
    </div>
  );
}

export default ProjectMembersTab;
